import { useState, type ReactNode } from 'react';
import { TAX_GROUP_LABEL, TAX_SOURCE_LABEL, TAX_SOURCES_PROVVISORIE, type TaxGroup, type TaxSource } from '@masamune/shared';
import { Chip } from '../../../components/ui/Chip';
import { Helper } from '../../../components/ui/Helper';
import { Icon } from '../../../components/ui/Icon';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { AnimatedNumber } from '../../../components/ui/AnimatedNumber';
import { SkeletonKPIRow, SkeletonTable } from '../../../components/ui/Skeleton';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { useModalStore } from '../../../stores/modal.store';
import { useAdminActions } from '../useAdminActions';
import { SettlePopover } from '../SettlePopover';
import { useConfermaPossibile, type RigaFisco, type StatoVersamento, type VoceFascia } from './fiscoApi';

/**
 * I pezzi di Fisco che si ripetono in tutte le viste: la frase, la fascia, i
 * due chip di una riga (stato del versamento e da dove viene la cifra), la
 * tabella delle tasse con le sue azioni, il blocco chiuso delle pagate.
 *
 * Etichette (SPEC, regola del 24/9): ogni titolo, colonna e bottone nomina la
 * cosa. Solo i chip dentro una riga restano brevi, perché la riga dice già di
 * quale tassa si parla.
 */

// ════════════════════════════════════════════════════════════════════
// Frase e fascia
// ════════════════════════════════════════════════════════════════════

export function Frase({ testo }: { testo: string | undefined }) {
  if (!testo) return null;
  return <p className="fis-frase">{testo}</p>;
}

const prima = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * La fascia: 2-3 somme della vista, mai uguali a una riga (lo garantisce
 * l'API). Dove il numero non c'è ("manca la cifra") o non è un importo
 * ("11 di 84") ci va la parola. `extra` aggiunge un comando sotto una voce.
 */
export function Fascia({ voci, extra }: { voci: VoceFascia[]; extra?: Partial<Record<string, ReactNode>> }) {
  if (!voci.length) return null;
  return (
    <div className="stat-band">
      {voci.map((v) => (
        <div key={v.key}>
          <div className="st-label">
            {v.label}
            {v.fonte && TAX_SOURCES_PROVVISORIE.includes(v.fonte) && v.valore != null && (
              <span className="chip warn adm-st-badge">{TAX_SOURCE_LABEL[v.fonte]}</span>
            )}
            {v.aiuto && <Helper text={v.aiuto} />}
          </div>
          {v.valore != null ? (
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={v.valore} format={v.valore < 1000 ? (n) => fmtEur(n, true) : fmtEur} /></div>
          ) : (
            <div className={`st-value fis-st-testo${/manca/i.test(v.testo ?? '') ? ' is-muto' : ''}`}>{prima(v.testo ?? '')}</div>
          )}
          {v.sotto && <div className={`st-delta${v.tono === 'alert' ? ' text-alert' : ''}`}>{v.sotto}</div>}
          {extra?.[v.key]}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Chip di una riga
// ════════════════════════════════════════════════════════════════════

const STATO_LABEL: Record<StatoVersamento, string> = {
  NON_VERSATA: 'Non versata in tempo',
  A_RATE: 'A rate',
  DA_PAGARE: 'Da pagare',
  PAGATA: 'Pagata',
  NEL_PIANO: 'Nel piano a rate',
};

export function StatoChip({ r }: { r: RigaFisco }) {
  const tone = r.stato === 'NON_VERSATA' ? 'alert'
    : r.stato === 'PAGATA' ? 'ok'
      : r.stato === 'A_RATE' ? 'info'
        : (r.giorni ?? 99) <= 7 ? 'warn' : 'default';
  return <Chip tone={tone} dot>{STATO_LABEL[r.stato]}</Chip>;
}

/** "Da dove viene la cifra": chip grigio, separato dallo stato, con la "i" per stime e nostri calcoli. */
export function FonteChip({ fonte, spiegazione }: { fonte: TaxSource; spiegazione?: string | null }) {
  return (
    <span className="inline-flex items-center">
      <Chip tone="ghost">{TAX_SOURCE_LABEL[fonte] ?? fonte}</Chip>
      {spiegazione && <Helper text={spiegazione} />}
    </span>
  );
}

/** Il tempo in parole, la data esatta nel tooltip. */
export function Quando({ r }: { r: RigaFisco }) {
  const esatta = r.stato === 'PAGATA' ? r.pagataIl : r.data;
  const cls = r.stato === 'NON_VERSATA' ? 'text-alert font-semibold' : r.stato !== 'PAGATA' && (r.giorni ?? 99) <= 7 ? 'text-warn' : '';
  return <span className={cls} title={esatta ? fmtDate(esatta) : undefined}>{r.quando || '—'}</span>;
}

// ════════════════════════════════════════════════════════════════════
// Tabella delle tasse
// ════════════════════════════════════════════════════════════════════

/**
 * Una tabella di tasse, una riga per versamento. Sul telefono diventa a schede
 * (`tbl--card`): la tassa in testa, poi quando, importo, stato e fonte, e i
 * bottoni in fondo alla scheda. `tbl--static`: la riga non apre niente, il
 * chevron sarebbe una promessa sbagliata.
 */
export function TabellaTasse({ righe, canWrite, colQuando = 'Quando si paga', colImporto = 'Importo da versare', senzaImporto, importoUguale, onVai }: {
  righe: RigaFisco[];
  canWrite: boolean;
  colQuando?: string;
  colImporto?: string;
  /** Il gruppo ha una riga sola: l'importo è già nell'intestazione del gruppo (un numero, una volta). */
  senzaImporto?: boolean;
  /** Le righe con questo importo non lo ripetono: è già nella fascia (la rata del piano di ogni mese). */
  importoUguale?: number | null;
  onVai?: (g: TaxGroup) => void;
}) {
  return (
    <table className="tbl tbl--ed tbl--card tbl--static fis-tbl">
      <thead>
        <tr>
          <th style={{ width: 150 }}>{colQuando}</th>
          <th>Tassa</th>
          <th className="num" style={{ width: 130 }}>{colImporto}</th>
          <th style={{ width: 175 }}>Stato del versamento</th>
          <th style={{ width: 165 }}>Da dove viene la cifra</th>
          <th style={{ width: 210 }} />
        </tr>
      </thead>
      <tbody>
        {righe.map((r) => <RigaTassa key={r.key} r={r} canWrite={canWrite} senzaImporto={senzaImporto} uguale={importoUguale != null && r.importo === importoUguale} onVai={onVai} colQuando={colQuando} colImporto={colImporto} />)}
      </tbody>
    </table>
  );
}

function RigaTassa({ r, canWrite, senzaImporto, uguale, onVai, colQuando, colImporto }: {
  r: RigaFisco; canWrite: boolean; senzaImporto?: boolean; uguale?: boolean; onVai?: (g: TaxGroup) => void; colQuando: string; colImporto: string;
}) {
  const openModal = useModalStore((s) => s.open);
  const { pay, remove } = useAdminActions();
  const conferma = useConfermaPossibile();
  const [pagaOpen, setPagaOpen] = useState(false);
  const aperta = r.stato !== 'PAGATA' && r.stato !== 'NEL_PIANO';
  // I trimestri dentro il piano a rate portano a Debiti a rate col loro chip: niente seconda freccia sulla riga.
  const vai = r.vai && onVai && r.stato !== 'NEL_PIANO' ? () => onVai(r.vai!) : undefined;
  const t = r.tax;

  const inserisci = () => t && openModal('newTax', { tax: t, prefill: { intent: 'cifra', source: 'CONFERMATO' } });
  const menu: ActionItem[] = canWrite && t ? [
    { key: 'cifra', label: 'Inserisci la cifra del commercialista', icon: 'pencil', hidden: !(aperta && r.inserisciCifra && r.importo != null), onClick: inserisci },
    { key: 'paga', label: 'Segna pagata a mano', icon: 'check', hidden: !(aperta && r.possibile && r.importo != null && r.installmentId), onClick: () => setPagaOpen(true) },
    { key: 'modifica', label: 'Modifica la tassa', icon: 'pencil', onClick: () => openModal('newTax', { tax: t }) },
    {
      key: 'elimina', label: 'Elimina la tassa', icon: 'trash', danger: true,
      onClick: () => remove.mutate({
        path: `/admin/taxes/${t.id}`,
        title: 'Eliminare questa tassa?',
        body: t.rate > 1 ? `${r.nome}, con tutte le sue ${t.rate} rate.` : r.nome,
        okTitle: 'Tassa eliminata',
      }),
    },
  ] : [];

  return (
    <tr className={vai ? 'fis-vai' : undefined} onClick={vai}>
      <td data-m="key" data-l={colQuando} className="fis-quando"><Quando r={r} /></td>
      <td data-m="hd">
        <div className="font-semibold flex items-center gap-2 flex-wrap">
          <span className="fis-vai-t">{r.nome}</span>
          {vai && <Icon name="chevronR" size={10} className="mute" />}
          {r.diAndrea && <Chip tone="ghost">Di Andrea</Chip>}
        </div>
        {r.dettaglio && <div className="text-[12px] mute">{r.dettaglio}</div>}
        {r.possibile && aperta && (
          <div className="text-[12px] mt-1">
            In banca: {r.possibile.testo}, € {fmtEur(r.possibile.totale, true)} in tutto
            {r.possibile.differenza > 0.005 && <span className="mute"> (con il ravvedimento, probabilmente)</span>}
          </div>
        )}
      </td>
      <td data-m={senzaImporto ? 'off' : 'key'} data-l={colImporto} className="num mono font-semibold">
        {senzaImporto ? null : uguale ? <span className="mute font-normal">la rata di ogni mese</span> : r.importo != null ? `€ ${fmtEur(r.importo, true)}` : aperta ? <Chip tone="warn">Manca la cifra</Chip> : '—'}
      </td>
      <td data-m="key" data-l="Stato del versamento">
        <div className="flex items-center gap-1.5 flex-wrap">
          {r.stato === 'NEL_PIANO' && onVai
            ? <Chip tone="info" onClick={() => onVai('RATE')}>Nel piano a rate ›</Chip>
            : <StatoChip r={r} />}
          {r.possibile && aperta && <Chip tone="info">Forse già pagata</Chip>}
        </div>
      </td>
      <td data-m="key" data-l="Da dove viene la cifra">
        {r.stato !== 'NEL_PIANO' && <FonteChip fonte={r.fonte} spiegazione={r.fonteSpiegazione} />}
      </td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end items-center gap-1 relative">
          {canWrite && aperta && r.possibile && r.installmentId && (
            <button className="btn btn-sm" disabled={conferma.isPending} onClick={() => conferma.mutate(r)} title="Collega i movimenti della banca a questa tassa e segnala pagata">
              <Icon name="check" size={10} /> Conferma il pagamento
            </button>
          )}
          {canWrite && aperta && !r.possibile && r.importo == null && t && (
            <button className="btn btn-sm" onClick={inserisci}>
              <Icon name="pencil" size={10} /> Inserisci la cifra
            </button>
          )}
          {canWrite && aperta && !r.possibile && r.importo != null && r.installmentId && (
            <button className="btn btn-sm" disabled={pay.isPending} onClick={() => setPagaOpen((v) => !v)}>
              <Icon name="check" size={10} /> Segna pagata
            </button>
          )}
          {vai && (
            <button className="btn btn-sm btn-ghost" onClick={vai}>
              {r.vai === 'IVA' ? "Apri l'IVA trimestre per trimestre" : `Vedi in ${TAX_GROUP_LABEL[r.vai!]}`} <Icon name="chevronR" size={10} />
            </button>
          )}
          {menu.length > 0 && <ActionsMenu items={menu} label={`Azioni su ${r.nome}`} />}
          {pagaOpen && r.installmentId && r.importo != null && (
            <SettlePopover
              mode="paga"
              amount={r.importo}
              target={{ kind: 'TAX_INSTALLMENT', id: r.installmentId }}
              counterparty={r.nome}
              side="DEBIT"
              pending={pay.isPending}
              onClose={() => setPagaOpen(false)}
              onConfirm={(v) => { setPagaOpen(false); pay.mutate({ source: 'TAX', id: r.installmentId!, amount: r.importo!, label: r.nome, ...v }); }}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

// ════════════════════════════════════════════════════════════════════
// Sezioni
// ════════════════════════════════════════════════════════════════════

/** Intestazione di un gruppo di righe: il nome della cosa, e a destra il suo totale o il conteggio. */
export function Intestazione({ label, destra }: { label: string; destra?: ReactNode }) {
  return (
    <div className="data-hd">
      <span className="t-micro">{label}</span>
      {destra != null && <div className="flex items-center gap-2">{destra}</div>}
    </div>
  );
}

export function Totale({ valore, nota }: { valore: number; nota?: string | null }) {
  return (
    <>
      {nota && <span className="mono mute text-[12px] d-only">{nota}</span>}
      <span className="mono fis-hd-tot">€ {fmtEur(valore, true)}</span>
    </>
  );
}

/** Il blocco chiuso in fondo (le pagate): il bottone dice cosa apre. */
export function Piega({ label, n, children, aperto: iniziale = false, filo = true }: { label: string; n?: number; children: ReactNode; aperto?: boolean; /** false quando sta subito sotto un'intestazione, che ha già il suo filo. */ filo?: boolean }) {
  const [aperto, setAperto] = useState(iniziale);
  return (
    <div className={filo ? 'adm-fold' : undefined}>
      <button className="btn btn-sm btn-ghost" aria-expanded={aperto} onClick={() => setAperto((v) => !v)}>
        <Icon name={aperto ? 'chevronU' : 'chevronD'} size={10} /> {aperto ? 'Nascondi' : 'Mostra'} {label}{n != null ? ` (${n})` : ''}
      </button>
      {aperto && <div className="mt-3">{children}</div>}
    </div>
  );
}

/** Le tasse già pagate: data di pagamento, tassa, importo versato, fonte. */
export function TabellaPagate({ righe, canWrite, importoUguale }: { righe: RigaFisco[]; canWrite: boolean; importoUguale?: number | null }) {
  return <TabellaTasse righe={righe} canWrite={canWrite} importoUguale={importoUguale} colQuando="Quando è stata pagata" colImporto="Importo versato" />;
}

export function Caricamento() {
  return (
    <>
      <SkeletonKPIRow count={3} />
      <table className="tbl tbl--ed"><tbody><SkeletonTable rows={5} cols={5} withAvatar={false} /></tbody></table>
    </>
  );
}
